/**
 * Kriegsakademie state: which lesson is open, whether its task is done, the step baseline, and
 * — persisted in localStorage — whether the player graduated or declined the first-launch offer.
 */
import { useStore } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { createStore } from 'zustand/vanilla';
import { type StepBaseline, TUTORIAL_STEPS } from './steps';

export interface TutorialState {
  /** The instructor's card is up (a Kriegsakademie match is running). */
  active: boolean;
  step: number;
  /** Index of the step whose task has been accomplished (its stamp shows), else -1. */
  doneStep: number;
  /** Baseline of the current step; captured by the driver once the view is available. */
  baseline: StepBaseline | null;
  /** performance.now() when the current step opened (minimum reading time). */
  openedAt: number;
  /** Persisted: graduated at least once. */
  completed: boolean;
  /** Persisted: the first-launch invitation was answered. */
  offerDismissed: boolean;

  begin(): void;
  goTo(step: number): void;
  next(): void;
  back(): void;
  markDone(step: number): void;
  setBaseline(b: StepBaseline): void;
  /** Close the card (the match continues). `graduated` records completion. */
  finish(graduated: boolean): void;
  dismissOffer(): void;
}

const now = () => (typeof performance !== 'undefined' ? performance.now() : 0);

export const tutorialStore = createStore<TutorialState>()(
  persist(
    (set, get) => ({
      active: false,
      step: 0,
      doneStep: -1,
      baseline: null,
      openedAt: 0,
      completed: false,
      offerDismissed: false,

      begin: () =>
        set({ active: true, step: 0, doneStep: -1, baseline: null, openedAt: now(), offerDismissed: true }),
      goTo: (step) => {
        const clamped = Math.max(0, Math.min(TUTORIAL_STEPS.length - 1, step));
        if (clamped === get().step) return;
        set({ step: clamped, doneStep: -1, baseline: null, openedAt: now() });
      },
      next: () => {
        const { step } = get();
        if (step >= TUTORIAL_STEPS.length - 1) get().finish(true);
        else get().goTo(step + 1);
      },
      back: () => get().goTo(get().step - 1),
      markDone: (step) => {
        if (get().step === step) set({ doneStep: step });
      },
      setBaseline: (baseline) => set({ baseline }),
      finish: (graduated) =>
        set((s) => ({ active: false, baseline: null, doneStep: -1, completed: s.completed || graduated })),
      dismissOffer: () => set({ offerDismissed: true }),
    }),
    {
      name: 'friction.academy',
      version: 1,
      storage: createJSONStorage(() => localStorage),
      partialize: ({ completed, offerDismissed }) => ({ completed, offerDismissed }),
    },
  ),
);

export function useTutorial<T>(selector: (s: TutorialState) => T): T {
  return useStore(tutorialStore, selector);
}
